/* eslint-disable no-unused-vars */
import * as pdf from '@/tools/pdf';
import Logo from "@/tools/pdf/components/logo";
import disclaimerData from "@/data/pdf-disclaimer.json";

// Init PdfMake
const pdfMake = require('@/../packages/pdfmake/pdfmake');
const pdfFonts = require('@/../packages/pdfmake/vfs_fonts.js')
pdfMake.fonts = {
  Helvetica: {
    normal: 'Helvetica',
    bold: 'Helvetica-Bold',
    italics: 'Helvetica-Oblique',
    bolditalics: 'Helvetica-BoldOblique'
  },
  HelveticaNeue: {
    normal: 'HelveticaNeueMed.ttf',
    light: 'HelveticaNeueLt.ttf',
    bold: 'HelveticaNeueBd.ttf',
  }
};
if (pdfMake.vfs == null) pdfMake.vfs = pdfFonts.pdfMake.vfs;

const {
  components: {Header, ContentSection, ContentCard, FooterP},
  macros: {entry, disclaimerA},
  svgs,
  styles,
  layouts,
  images,
  config,
} = pdf;

const defHeaderArgs = {
  title: 'Let’s look at your numbers',
};

class PdfBuilder {
  pages = [];

  constructor(results) {
    this.results = results;
  }

  async build() {
    const {user} = this.results;

    if (user.schoolKnown === 'yes' && (user.majorKnown === 'yes' || user.skills.length > 0)) {
      this.renderMajorsAndSchools();
    } else if (user.careerKnown === 'yes' && (user.schools.length > 0 || user.type !== 'unsure' || user.state)) {
      this.renderCareerAndSchools();
    } else if (user.type !== 'unsure' || user.state || user.schools.length > 0) {
      this.renderMajorsAndGeneralSchool();
    } else /* if (user.type === 'unsure' && !user.state && user.schools.length === 0) */ {
      this.renderUnsureEverything();
    }

    return pdfMake.createPdf({
      pageSize: 'LETTER',
      pageMargins: config.vars.pageMargins,
      // info: { title, author, subject, keywords, },  // https://pdfmake.github.io/docs/0.1/document-definition-object/document-medatadata/
      defaultStyle: styles.default,
      styles,
      layouts,
      images,
      content: [
        ...this.processPages(),
        this.renderDisclaimerPage()
      ],
      footer: this.getFooter,
      header: this.getHeader,
    });
  }

  renderMajorsAndSchools() {
    const {majorCareers, user: {schools}} = this.results;
    const {title} = defHeaderArgs;

    majorCareers.forEach(majorCareer => {
      const footNotes = [];
      const cards = [
        this.renderMajorCard(majorCareer)
      ].concat(schools.map((school, idx) => this.renderSchoolCard(school, majorCareer.major, {idx: idx + 1})));

      this.addPage([
        Header({
          title,
        }),
        ContentSection(cards, {
          title: majorCareer.major
        })
      ], footNotes)
    })
  }

  renderCareerAndSchools() {
    const {user: {schools}, majorCareers} = this.results;
    const {title} = defHeaderArgs;
    const firstMajor = majorCareers[0];

    this.addPage([
      Header({
        title,
      }, {hero: true}),
      this.renderMajorSection(
        firstMajor,
        schools.length ? schools.slice(0, 3) : [],
        {startIdx: 1}
      )
    ]);

    const perPage = 4;
    const remainingSchools = schools.slice(3);
    for (let i = 0; i < remainingSchools.length; i += perPage) {
      const pageSchools = remainingSchools.slice(i, i + perPage);

      this.addPage([
        Logo(undefined, {margin: [0, -20, 0, 20], alignment: 'left'}),
        ContentSection(
          pageSchools.map(
            (school, idx) => this.renderSchoolCard(school, firstMajor.major, {idx: 2 + idx})
          )
        )
      ]);
    }
  }

  renderMajorsAndGeneralSchool() {
    const {majorCareers} = this.results;
    const {title} = defHeaderArgs;

    const firstMajor = majorCareers[0];

    this.addPage([
      Header({
        title,
      }, {hero: true}),
      this.renderMajorSection(firstMajor)
    ]);

    const rest = majorCareers.slice(1);
    const perPage = 2;

    for (let i = 0; i < rest.length; i += perPage) {
      const majors = rest.slice(i, i + perPage);
      const cards = majors.map(major => this.renderMajorSection(major, [], {sectionOptions: {marginBottom: 40}}));
      this.addPage([{
        stack: [
          Logo(undefined, {margin: [0, -20, 0, 20], alignment: 'left'}),
          cards
        ]
      }]);
    }
  }

  renderUnsureEverything() {
    // Cover Page
    this.addPage([
      Header({
        title: 'Take your future for a test drive. ',
        description: 'As you begin to explore your options, this might help you think about what kind of schools, major and careers you want to pursue.',
      }, {
        hero: true,
        marginBottom: 0,
        marginTop: -20
      }),
      ContentSection([
        ContentCard([
          entry('Private School Annual Cost Of Attendance', '$52,590'),
          entry('Public School Annual Cost Of Attendance', '$20,500'),
        ], {
          type: 'big-number',
          title: ['Public or private?', disclaimerA(7)],
          description: 'When comparing sticker prices, private schools will almost always have a higher cost than public schools. Many private institutions offer generous merit scholarships and aid packages to those who qualify.',
          icon: {
            svg: svgs.school
          },
          iconPosition: 'top',
          iconWidth: 44,
          gridValueStyle: 'cardGridValueBig'
        }),
        ContentCard([
          entry('Public 4-Year In-State On-Campus', '$10,940'),
          entry('Public 4-Year Out-Of-State On-Campus', '$28,240'),
          entry('Private Nonprofit 4-Year On-Campus', '$39,400'),
        ], {
          type: 'big-number',
          title: ['In-state or out-of-state?', disclaimerA(8)],
          description: 'It is a common belief that out-of-state tuition can be more expensive than in-state tuition. As you evaluate college options, it’s important to look at costs beyond tuition. Here’s an estimated budget  for full-time undergraduate students.',
          icon: {
            svg: svgs.car
          },
          iconPosition: 'top',
          iconWidth: 44,
          gridValueStyle: 'cardGridValueBig'
        }),
      ], {
        infBottom: true,
        fullWidth: true,
        paddingTop: 48,
        paddingLeft: 20,
        paddingRight: 20,
        paddingBottom: 0,
      })
    ]);

    // Split majors to pages
    const {majorCareers} = this.results;
    const perPage = 4;

    for (
      let i = 0, pageNum = 1;
      i < majorCareers.length;
      i += perPage, pageNum++
    ) {
      const currentPage = [];
      const footNotes = [];
      const majors = majorCareers.slice(i, i + perPage);

      if (pageNum === 1) {
        currentPage.push(Header(
          {
            title: 'A look at some majors you might like.',
          },
          {
            marginBottom: 30,
          }
        ))
      }

      currentPage.push(ContentSection(majors.map(
        m => this.renderMajorCard(m, {sectionTitle: m.major})
      )));

      this.addPage(currentPage, footNotes);
    }
  }

  addPage(content, footNotes) {
    this.pages.push({content, footNotes});
  }

  getHeader = (currentPage, pageCount, pageSize) => {

  }

  getFooter = (currentPage, pageCount) => {
    const rows = [];
    const isFirst = currentPage === 1;

    if (isFirst) {
      rows.push(
        FooterP(
          {
            text: [
              `This tool provides estimates intended for use only as a planning guide. We do not guarantee its accuracy or applicability to your circumstance, and we encourage you to consult a qualified professional, school or financial aid office directly for more information. Information within this tool is based on a review of government information. College and university costs, median post-graduation salaries and federal loan data is sourced from the 2023 US Department of Education College Scorecard. Major and career categories are sourced from the 2021 US Census American Community Survey. Data is based on 4-year colleges and universities offering a bachelor’s degree. Discover Student Loans does not offer federal student loans and this resource is not affiliated with the Department of Education. We will not share your results.`,
              ' ',
              {
                text: 'Terms of Use',
                link: 'https://www.discover.com/credit-cards/help-center/discover-terms-of-use.html',
                style: 'disclaimer'
              },
              '.'
            ]
          },
          {marginTop: 0}
        )
      )
    }

    return {
      columns: [
        {
          text: rows,
          style: 'disclaimer',
          width: '*',
          margin: [config.vars.pageMarginL, 0, 0, config.vars.pageMarginB]
        },
        {
          text: currentPage.toString(),
          style: 'disclaimer',
          width: 'auto',
          margin: [0, 15, config.vars.pageMarginR, config.vars.pageMarginB]
        }
      ]
    }
  }

  processPages() {
    return this.pages.map(({content, footNotes}, idx, pages) => {
      // Set page break
      if (content.length) {
        content[content.length - 1].pageBreak = 'after';
      }

      // Process foot notes ?

      return content;
    });
  }

  renderMajorCard(major, options = {}) {
    const {normalizeMoney} = this.results;
    const {sectionTitle} = options;

    return ContentCard(
      major.careers.map(
        career => entry(career.occ_code, normalizeMoney(career.med_earn))
      ),
      {
        type: 'row',
        icon: {svg: svgs.briefcase},
        title: [`Top careers and\nexpected salaries`, disclaimerA(1)],
        sectionTitle
      }
    )
  }

  renderMajorCostCard(major, options = {}) {
    const {normalizeMoney, averageTuition, average} = this.results;
    const {
      MD_EARN_WNE_P6: medianSalary,
      GRAD_DEBT_MDN: expectedMedian,
      GRAD_DEBT_MDN10YR: expectedPayment
    } = average;

    return ContentCard(
      [
        entry(['Expected annual cost of attendance (with aid)', disclaimerA(2)], normalizeMoney(averageTuition)),
        entry(['Median salary in this state', disclaimerA(3)], normalizeMoney(medianSalary)),
        entry(['Expected median federal loan debt', disclaimerA(5)], normalizeMoney(expectedMedian)),
        entry(['Expected monthly loan payment over 10 years', disclaimerA(6)], normalizeMoney(expectedPayment) + (expectedPayment !== 0 ? '/mo' : '')),
      ],
      {
        type: 'grid',
        title: 'College Cost Breakdown',
        ...options
      }
    )
  }

  renderSchoolCard(school, major = null, cardOptions = {}) {
    const {normalizeMoney, hasEarningsOutcome, getOutcome} = this.results;
    const {
      MD_EARN_WNE_P6: medianSalary,
      GRAD_DEBT_MDN: averageDebt,
      GRAD_DEBT_MDN10YR: expectedPayment
    } = school;

    const medianMajorSalary = hasEarningsOutcome(school.UNITID, major) ? getOutcome(school.UNITID, major)['MD_EARN_WNE'] : null;

    var salaryEntry;
    if (medianMajorSalary) {
        salaryEntry = entry(['Median salary for this major at this school', disclaimerA(4)], normalizeMoney(medianMajorSalary));
    }
    else {
        salaryEntry = entry(['Median salary at this school', disclaimerA(3)], normalizeMoney(medianSalary));
    }

    return ContentCard(
      [
        entry(['Expected annual cost of attendance (with aid)', disclaimerA(2)], normalizeMoney(school.NPT4_PUB || school.NPT4_PRIV)),
        salaryEntry,
        entry(['Total average federal loan debt', disclaimerA(5)], normalizeMoney(averageDebt)),
        entry(['Monthly loan payment over 10 years', disclaimerA(6)], normalizeMoney(expectedPayment) + (expectedPayment !== 0 ? '/mo' : ''))
      ],
      {
        type: 'grid',
        title: school.INSTNM,
        ...cardOptions
      }
    )
  }

  renderMajorSection(major, schools = [], {startIdx = 0, sectionOptions = {}} = {}) {
    const cards = [
      this.renderMajorCard(major)
    ];

    if (schools.length) {
      schools.forEach(
        (school, idx) => cards.push(this.renderSchoolCard(school, major.major, {idx: startIdx && startIdx + idx}))
      );
    } else {
      cards.push(this.renderMajorCostCard(major,));
    }

    return ContentSection(cards, {
      title: major.major,
      ...(sectionOptions || {})
    });
  }

  renderDisclaimerPage() {
    const entries = disclaimerData.map(
      ([title, ...paragraphs]) => ([
        {
          text: title,
          style: 'disclaimerT'
        },
        ...paragraphs.map(paragraph => ({
          text: paragraph,
          style: 'disclaimerP'
        })),
        {text: '', margin: [0, 0, 0, 20]}
      ])
    );

    const splitAt = 4;

    return [
      Logo(undefined, {margin: [0, -20, 0, 20], alignment: 'left'}),
      {
        columns: [
          entries.slice(0, splitAt),
          entries.slice(splitAt),
        ],
      }
    ];
  }
}

export default async function () {
  return (new PdfBuilder(this)).build();
}
